import { FC, useEffect, useState } from "react";
import * as d3 from "d3";
import * as am5 from "@amcharts/amcharts5";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
//Type of the chart
import * as am5xy from "@amcharts/amcharts5/xy";
import { MinMaxUnit } from "../../../pages/simpleapp/components/SimpleAppTemplate";
import {
  getLineColor,
  TemperatureDisplayUnit,
  ColorVariables,
  getChartDateTime,
} from "../../../pages/simpleapp/components/widgets/WidgetHelper";
import { SingleTemperatureHistoricalData } from "../../../pages/simpleapp/components/widgets/historic/hooks/useHistoricalSingleTemperature";

type DataModel = {
  time: string;
  temperature: number;
  minTemperature: number;
  maxTemperature: number;
  lineStrokeSetting: {
    stroke: string;
  };
  lineFillSetting: {
    fill: string;
  };
};

type Props = {
  data: SingleTemperatureHistoricalData[];
  temperatureLabel: string;
  chartId: string;
};

const AMCSingleTemperature: FC<Props> = ({ data, temperatureLabel, chartId }) => {
  const [normalizedData, setNormalizedData] = useState<DataModel[]>([]);

  // Default
  const [temperatureData, setTemperatureData] = useState<MinMaxUnit>({
    min: 0,
    max: 75,
    unit: "°C",
  });

  useEffect(() => {
    if (data) {
      let displayUnit = TemperatureDisplayUnit.Undefined;
      switch (data[0].temperatureDisplayUnit) {
        case "°C":
          setTemperatureData({ min: 0, max: 75, unit: data[0].temperatureDisplayUnit });
          displayUnit = TemperatureDisplayUnit.Celsius;
          break;
        case "°F":
          setTemperatureData({ min: 32, max: 167, unit: data[0].temperatureDisplayUnit });
          displayUnit = TemperatureDisplayUnit.Fahrenheit;
          break;
        case "°K":
          setTemperatureData({ min: 273, max: 348, unit: data[0].temperatureDisplayUnit });
          displayUnit = TemperatureDisplayUnit.Kelvin;
          break;
      }
      const dataArr: DataModel[] = [];

      data.map((datum) => {
        dataArr.push({
          time: getChartDateTime(datum.time),
          temperature: datum.temperature,
          minTemperature: datum.minTemperature,
          maxTemperature: datum.maxTemperature,
          lineStrokeSetting: { stroke: getLineColor(datum.temperature, displayUnit) },
          lineFillSetting: { fill: getLineColor(datum.temperature, displayUnit) },
        });
      });

      setNormalizedData(dataArr);
    }
  }, [data]);

  useEffect(() => {
    if (!!chartId && !!normalizedData && normalizedData.length > 0) {
      const root = am5.Root.new(chartId);

      // Set themes (https://www.amcharts.com/docs/v5/concepts/themes/)
      root.setThemes([am5themes_Animated.new(root)]);

      // Create Chart (https://www.amcharts.com/docs/v5/charts/xy-chart/)
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: false,
          wheelY: "zoomX",
          layout: root.verticalLayout,
        }),
      );

      // Add cursor: In here you can configure how the cursor is displayed and how it behaves on the chart.
      const cursor = chart.set(
        "cursor",
        am5xy.XYCursor.new(root, {
          behavior: "none",
        }),
      );
      cursor.lineY.setAll({
        stroke: am5.color(ColorVariables.colorPlaceholder),
        visible: true,
      });
      cursor.lineX.setAll({
        stroke: am5.color(ColorVariables.colorPlaceholder),
        visible: true,
      });

      // Create axes https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
      // For the X-Axis
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "time",
          marginTop: 15,
          maxDeviation: 0,
          renderer: am5xy.AxisRendererX.new(root, {}),
        }),
      );
      xAxis.data.setAll(normalizedData);

      // For the Y-Axis
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          // min: temperatureData.min,
          // max: temperatureData.max,
          numberFormat: `#'${temperatureData.unit}'`,
          maxPrecision: 0,
          renderer: am5xy.AxisRendererY.new(root, { opposite: false }),
        }),
      );
      yAxis.get("renderer").grid.template.set("forceHidden", true);

      //### For the Actual Temperature ###//
      //Line Series - the data
      const tempLineLast = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: temperatureLabel,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "temperature",
          categoryXField: "time",
          curveFactory: d3.curveBumpX,
          tooltip: am5.Tooltip.new(root, {
            labelText: `[bold]${temperatureLabel}[/]: {valueY}${temperatureData.unit} \n\n[bold]DateTime[/]: {categoryX}`,
            dy: -5,
          }),
        }),
      );
      tempLineLast.strokes.template.setAll({
        templateField: "lineStrokeSetting",
        strokeWidth: 1,
      });
      tempLineLast.data.setAll(normalizedData);
      tempLineLast.appear(1000);

      // create hover state for series and for mainContainer, so that when series is hovered,
      // the state would be passed down to the strokes which are in mainContainer.
      tempLineLast.set("setStateOnChildren", true);
      tempLineLast.states.create("hover", {});

      tempLineLast.mainContainer.set("setStateOnChildren", true);
      tempLineLast.mainContainer.states.create("hover", {});

      tempLineLast.strokes.template.states.create("hover", {
        strokeWidth: 3,
      });

      //### For the MinMax Temperature ###//
      const tempAreaMin = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: temperatureLabel,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "minTemperature",
          categoryXField: "time",
          curveFactory: d3.curveBumpX,
          tooltip: am5.Tooltip.new(root, {
            labelText: `[bold]Min ${temperatureLabel}[/]: {valueY}${temperatureData.unit} \n\n[bold]DateTime[/]: {categoryX}`,
            dy: -5,
          }),
        }),
      );

      const tempAreaMax = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: `Min-Max ${temperatureLabel}`,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "maxTemperature",
          openValueYField: "minTemperature",
          categoryXField: "time",
          curveFactory: d3.curveBumpX,
          stroke: tempAreaMin.get("stroke"),
          fill: tempAreaMin.get("stroke"),
          tooltip: am5.Tooltip.new(root, {
            labelText: `[bold]Max ${temperatureLabel}[/]: {valueY}${temperatureData.unit} \n\n[bold]DateTime[/]: {categoryX}`,
            dy: -5,
          }),
        }),
      );
      tempAreaMax.fills.template.setAll({
        templateField: "lineFillSetting",
        visible: true,
        opacity: 0.4,
      });

      tempAreaMin.strokes.template.setAll({
        templateField: "lineStrokeSetting",
        strokeWidth: 1,
      });
      tempAreaMax.strokes.template.setAll({
        templateField: "lineStrokeSetting",
        strokeWidth: 1,
      });

      tempAreaMin.data.setAll(normalizedData);
      tempAreaMax.data.setAll(normalizedData);
      tempAreaMin.appear(1000);
      tempAreaMax.appear(1000);

      // Only show one legend for both
      tempAreaMin.events.once("datavalidated", () => {
        tempAreaMin.get("legendDataItem")?.get("itemContainer").hide();
      });
      tempAreaMax.events.on("boundschanged", () => {
        if (tempAreaMax.isHidden()) {
          tempAreaMin.hide();
        } else {
          tempAreaMin.show();
        }
      });
      // tempAreaMax.events.once("datavalidated", () => {
      //    tempAreaMax.get("legendDataItem")?.get("itemContainer").hide();
      // });

      // Add scrollbar
      const scrollbar = chart.set(
        "scrollbarX",
        am5.Scrollbar.new(root, {
          orientation: "horizontal",
          start: 0.8,
          height: 10,
          marginTop: 15,
        }),
      );
      scrollbar.thumb.setAll({
        fill: am5.color(ColorVariables.colorPlaceholder),
      });
      scrollbar.startGrip.setAll({
        visible: false,
      });
      scrollbar.endGrip.setAll({
        visible: false,
      });
      chart.bottomAxesContainer.children.push(scrollbar);

      // Add Legends
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
        }),
      );

      // Make series change state when legend item is hovered
      legend.itemContainers.template.states.create("hover", {});

      legend.itemContainers.template.events.on("pointerover", (e) => {
        const target: any = e.target.dataItem?.dataContext;
        target.hover();
      });
      legend.itemContainers.template.events.on("pointerout", (e) => {
        const target: any = e.target.dataItem?.dataContext;
        target.unhover();
      });

      legend.data.setAll(chart.series.values);

      // Make stuff animate on load (https://www.amcharts.com/docs/v5/concepts/animations/)
      chart.appear(1000, 100);

      return () => {
        root.dispose();
      };
    }
  }, [chartId, normalizedData]);

  return <div className="am-chart-container" id={chartId} />;
};

export default AMCSingleTemperature;
